import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  Clock,
  Shield
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AlertsPage = () => {
  const { getAuthHeader } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("unresolved");

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { resolved: filter === "resolved" };
      const response = await axios.get(`${API}/alerts`, {
        headers: getAuthHeader(),
        params
      });
      setAlerts(response.data);
    } catch (error) {
      toast.error("Failed to fetch alerts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAlerts();
  }, [filter]);

  const handleResolve = async (alertId) => {
    try {
      await axios.post(`${API}/alerts/${alertId}/resolve`, {}, {
        headers: getAuthHeader()
      });
      toast.success("Alert resolved");
      fetchAlerts();
    } catch (error) {
      toast.error("Failed to resolve alert");
    }
  };

  const getSeverityBadge = (severity) => {
    const variants = {
      low: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20",
      medium: "bg-amber-500/10 text-amber-500 border-amber-500/20",
      high: "bg-orange-500/10 text-orange-500 border-orange-500/20",
      critical: "bg-red-500/10 text-red-500 border-red-500/20"
    };
    return variants[severity] || variants.low;
  };

  const unresolvedCount = alerts.filter(a => !a.resolved).length;

  return (
    <div className="space-y-6" data-testid="alerts-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-mono font-bold tracking-tight flex items-center gap-3">
            <AlertTriangle className="w-7 h-7 text-amber-500" />
            Threat Alerts
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {unresolvedCount} unresolved alert{unresolvedCount !== 1 ? 's' : ''}
          </p>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={fetchAlerts}
          className="gap-2"
          data-testid="refresh-alerts-btn"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Filter Buttons */}
      <div className="flex gap-2">
        <Button
          variant={filter === "unresolved" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilter("unresolved")}
          data-testid="filter-unresolved"
        >
          Unresolved
        </Button>
        <Button
          variant={filter === "resolved" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilter("resolved")}
          data-testid="filter-resolved"
        >
          Resolved
        </Button>
        <Button
          variant={filter === "all" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilter("all")}
          data-testid="filter-all"
        >
          All
        </Button>
      </div>

      {/* Alerts List */}
      <div className="space-y-4" data-testid="alerts-list">
        {loading ? (
          [...Array(3)].map((_, i) => (
            <Card key={i} className="bg-card/50 border-border">
              <CardContent className="p-4">
                <div className="skeleton h-16 w-full" />
              </CardContent>
            </Card>
          ))
        ) : alerts.length > 0 ? (
          alerts.map((alert) => (
            <Card 
              key={alert.id} 
              className={`bg-card/50 border-border transition-all ${
                !alert.resolved ? "border-l-4 border-l-amber-500" : ""
              }`}
              data-testid={`alert-${alert.id}`}
            >
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-3">
                      <Shield className={`w-5 h-5 ${alert.resolved ? "text-muted-foreground" : "text-amber-500"}`} />
                      <span className="font-mono text-primary">{alert.ip_address}</span>
                      <Badge className={`text-xs uppercase ${getSeverityBadge(alert.severity)}`}>
                        {alert.severity}
                      </Badge>
                      {alert.resolved && (
                        <Badge className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20 text-xs">
                          Resolved
                        </Badge>
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{alert.threat_type}</p>
                      <p className="text-sm text-muted-foreground">{alert.description}</p>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Clock className="w-3 h-3" />
                      {new Date(alert.timestamp).toLocaleString()}
                    </div>
                  </div>
                  {!alert.resolved && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2 hover:bg-emerald-500/10 hover:text-emerald-500 hover:border-emerald-500/20"
                      onClick={() => handleResolve(alert.id)}
                      data-testid={`resolve-alert-${alert.id}`}
                    >
                      <CheckCircle className="w-4 h-4" />
                      Resolve
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <Card className="bg-card/50 border-border">
            <CardContent className="p-8 text-center">
              <AlertTriangle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No alerts found</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AlertsPage;
